import React, { useEffect, useRef, useState } from "react";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";
import { MapContainer, Marker, Popup } from "react-leaflet";
import BaseMapLayer from "../../../components/molecules/Map/BaseMapLayer";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getDesconexiones, updateObservacionDesconexion, marcarSolucionadoDesconexion } from "./integracion";
import { formatearFechaGuion } from "../../../utilidades/formateos";
import "./Desconexiones.scss";

// Corregir íconos de Leaflet con webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// ── Helpers de fecha ────────────────────────────────────────────
const getPrimerDiaMes = () => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
};

const getUltimoDiaMes = () => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
};

// ── Componente principal ────────────────────────────────────────
const Desconexiones = () => {
    const toast = useRef(null);

    // Filtros
    const [filtros, setFiltros] = useState({
        desde: getPrimerDiaMes(),
        hasta: getUltimoDiaMes(),
    });

    // Data
    const [datos, setDatos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalFilterValue, setGlobalFilterValue] = useState("");

    // Modal mapa
    const [modalMapaVisible, setModalMapaVisible] = useState(false);
    const [registroMapa, setRegistroMapa] = useState(null);

    // Modal comentario
    const [modalComentarioVisible, setModalComentarioVisible] = useState(false);
    const [registroComentario, setRegistroComentario] = useState(null);
    const [comentario, setComentario] = useState("");
    const [guardandoComentario, setGuardandoComentario] = useState(false);

    // Switch solucionado
    const [togglingId, setTogglingId] = useState(null);

    // ── Cargar datos ────────────────────────────────────────────
    const buscar = async () => {
        setLoading(true);
        try {
            const params = {
                fecha_inicio: formatearFechaGuion(filtros.desde),
                fecha_fin: formatearFechaGuion(filtros.hasta),
            };
            const response = await getDesconexiones(params);
            setDatos(response ?? []);
        } catch (error) {
            console.error("Error al cargar desconexiones:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "No se pudieron cargar las desconexiones.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const limpiar = () => {
        setFiltros({ desde: getPrimerDiaMes(), hasta: getUltimoDiaMes() });
        setGlobalFilterValue("");
        setDatos([]);
    };

    useEffect(() => {
        buscar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Toggle solucionado ────────────────────────────────────
    const toggleSolucionado = async (rowData, nuevoValor) => {
        console.log("rowData",rowData);
        
        setTogglingId(rowData.ID);
        try {
            await marcarSolucionadoDesconexion({
                id_desconexion: rowData.ID,
                solucionado: nuevoValor ? 1 : 0,
            });
            setDatos((prev) =>
                prev.map((d) =>
                    d.ID === rowData.ID ? { ...d, SOLUCIONADO: nuevoValor ? 1 : 0 } : d
                )
            );
            toast.current?.show({
                severity: nuevoValor ? "success" : "warn",
                summary: nuevoValor ? "Solucionado" : "Pendiente",
                detail: `Desconexión #${rowData.ID} marcada como ${nuevoValor ? "solucionada" : "pendiente"}.`,
                life: 3000,
            });
        } catch {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "No se pudo actualizar el estado.",
                life: 3000,
            });
        } finally {
            setTogglingId(null);
        }
    };

    // ── Abrir modal mapa ────────────────────────────────────────
    const abrirMapa = (rowData) => {
        setRegistroMapa(rowData);
        setModalMapaVisible(true);
    };

    // ── Abrir modal comentario ──────────────────────────────────
    const abrirComentario = (rowData) => {
        setRegistroComentario(rowData);
        setComentario(rowData.OBSERVACIONES ?? "");
        setModalComentarioVisible(true);
    };

    const guardarComentario = async () => {
        if (!registroComentario) return;
        setGuardandoComentario(true);
        try {
            await updateObservacionDesconexion({
                id_desconexion: registroComentario.ID,
                comentario: comentario,
            });
            // Actualizar dato local
            setDatos((prev) =>
                prev.map((d) =>
                    d.ID === registroComentario.ID
                        ? { ...d, OBSERVACIONES: comentario }
                        : d
                )
            );
            toast.current?.show({
                severity: "success",
                summary: "Guardado",
                detail: "Observación actualizada correctamente.",
                life: 3000,
            });
            setModalComentarioVisible(false);
        } catch {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "No se pudo guardar la observación.",
                life: 3000,
            });
        } finally {
            setGuardandoComentario(false);
        }
    };

    // ── Templates de columnas ───────────────────────────────────
    const isSolucionado = (val) =>
        val === 1 || val === "1" || val === true || String(val ?? "").toUpperCase() === "SI";

    const solucionadoTemplate = (rowData) => {
        const checked = isSolucionado(rowData.SOLUCIONADO);
        const cargando = togglingId === rowData.ID;
        return (
            <div className="desc-switch-cell">
                <InputSwitch
                    checked={checked}
                    onChange={(e) => toggleSolucionado(rowData, e.value)}
                    disabled={cargando}
                />
                <span className={checked ? "desc-badge-si" : "desc-badge-no"}>
                    {checked ? "Sí" : "No"}
                </span>
            </div>
        );
    };

    const coordenadasTemplate = (rowData) => {
        if (!rowData.LATITUD || !rowData.LONGITUD)
            return <span className="desc-sin-coords">—</span>;
        return (
            <button className="desc-btn-mapa" onClick={() => abrirMapa(rowData)}>
                <i className="pi pi-map-marker" />
                Ver mapa
            </button>
        );
    };

    const accionesTemplate = (rowData) => (
        <Button
            icon="pi pi-comment"
            rounded
            text
            severity="info"
            tooltip="Agregar / editar observación"
            tooltipOptions={{ position: "left" }}
            onClick={() => abrirComentario(rowData)}
            size="small"
        />
    );

    // ── Header de la tabla ──────────────────────────────────────
    const header = (
        <div className="desc-table-header">
            {/* <span className="desc-table-title">
                <i className="pi pi-wifi" style={{ color: "#D13438" }} />
                Registro de Desconexiones
            </span> */}
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText
                    value={globalFilterValue}
                    onChange={(e) => setGlobalFilterValue(e.target.value)}
                    placeholder="Buscar..."
                    className="desc-global-filter"
                />
            </IconField>
        </div>
    );

    // ── Footer modal comentario ─────────────────────────────────
    const footerComentario = (
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <Button
                label="Cancelar"
                icon="pi pi-times"
                severity="secondary"
                onClick={() => setModalComentarioVisible(false)}
                disabled={guardandoComentario}
            />
            <Button
                label="Guardar"
                icon="pi pi-check"
                onClick={guardarComentario}
                loading={guardandoComentario}
            />
        </div>
    );

    // ── Render ──────────────────────────────────────────────────
    return (
        <div className="desc-container">
            <Toast ref={toast} />

            {/* ── FILTROS ── */}
            <div className="desc-filtros">
                <div className="grupo-filtro">
                    <label>Fecha inicio</label>
                    <Calendar
                        value={filtros.desde}
                        onChange={(e) => setFiltros({ ...filtros, desde: e.value })}
                        dateFormat="dd/mm/yy"
                        showIcon
                        className="w-full"
                    />
                </div>
                <div className="grupo-filtro">
                    <label>Fecha fin</label>
                    <Calendar
                        value={filtros.hasta}
                        onChange={(e) => setFiltros({ ...filtros, hasta: e.value })}
                        dateFormat="dd/mm/yy"
                        showIcon
                        className="w-full"
                    />
                </div>
                <div className="grupo-filtro desc-botones">
                    <Button
                        icon="pi pi-search"
                        label="Buscar"
                        onClick={buscar}
                        loading={loading}
                        size="small"
                    />
                    <Button
                        icon="pi pi-times"
                        label="Limpiar"
                        severity="secondary"
                        onClick={limpiar}
                        size="small"
                    />
                </div>
            </div>

            {/* ── DATATABLE ── */}
            <DataTable
                value={datos}
                loading={loading}
                header={header}
                globalFilter={globalFilterValue}
                emptyMessage="Sin registros. Aplica un filtro para buscar."
                stripedRows
                resizableColumns
                columnResizeMode="expand"
                scrollable
                scrollHeight="calc(100vh - 260px)"
                virtualScrollerOptions={{ itemSize: 40 }}
                size="small"
                className="desc-datatable"
                paginator
                rows={50}
                rowsPerPageOptions={[25, 50, 100]}
            >
                <Column field="ID" header="ID" sortable style={{ minWidth: "5rem" }} />
                <Column field="FECHA" header="Fecha" sortable style={{ minWidth: "11rem" }} />
                <Column
                    field="ID_UNIDAD"
                    header="Unidad"
                    sortable
                    style={{ minWidth: "6rem" }}
                />
                <Column
                    field="OPERADOR"
                    header="Operador"
                    sortable
                    style={{ minWidth: "14rem" }}
                />
                <Column
                    field="ASUNTO"
                    header="Asunto"
                    sortable
                    style={{ minWidth: "12rem" }}
                />
                <Column
                    field="OBSERVACIONES"
                    header="Observaciones"
                    style={{ minWidth: "16rem" }}
                />
                <Column
                    field="SOLUCIONADO"
                    header="Solucionado"
                    body={solucionadoTemplate}
                    sortable
                    style={{ minWidth: "8rem" }}
                />
                <Column
                    header="Ubicación"
                    body={coordenadasTemplate}
                    style={{ minWidth: "8rem" }}
                />
                <Column
                    header="Acciones"
                    body={accionesTemplate}
                    style={{ minWidth: "6rem", textAlign: "center" }}
                />
            </DataTable>

            {/* ── MODAL MAPA ── */}
            <Dialog
                header={
                    <div className="desc-modal-header">
                        <span className="desc-modal-header-icon desc-modal-header-icon--red">
                            <i className="pi pi-map-marker" />
                        </span>
                        <div>
                            <div className="desc-modal-header-title">Ubicación de desconexión</div>
                            {registroMapa && (
                                <div className="desc-modal-header-sub">
                                    Unidad {registroMapa.ID_UNIDAD} · {registroMapa.FECHA}
                                </div>
                            )}
                        </div>
                    </div>
                }
                visible={modalMapaVisible}
                onHide={() => setModalMapaVisible(false)}
                style={{ width: "min(760px, 97vw)" }}
                contentClassName="desc-modal-mapa"
                draggable={false}
                resizable={false}
            >
                {registroMapa && (
                    <>
                        {/* Fichas de datos */}
                        <div className="desc-mapa-chips">
                            <div className="desc-chip">
                                <i className="pi pi-car desc-chip-icon" />
                                <div>
                                    <span className="desc-chip-label">Unidad</span>
                                    <span className="desc-chip-val">{registroMapa.ID_UNIDAD ?? "—"}</span>
                                </div>
                            </div>
                            <div className="desc-chip">
                                <i className="pi pi-user desc-chip-icon" />
                                <div>
                                    <span className="desc-chip-label">Operador</span>
                                    <span className="desc-chip-val">{registroMapa.OPERADOR ?? "—"}</span>
                                </div>
                            </div>
                            <div className="desc-chip">
                                <i className="pi pi-tag desc-chip-icon" />
                                <div>
                                    <span className="desc-chip-label">Asunto</span>
                                    <span className="desc-chip-val">{registroMapa.ASUNTO ?? "—"}</span>
                                </div>
                            </div>
                            <div className="desc-chip">
                                <i className="pi pi-check-circle desc-chip-icon" />
                                <div>
                                    <span className="desc-chip-label">Solucionado</span>
                                    <span className={isSolucionado(registroMapa.SOLUCIONADO) ? "desc-badge-si" : "desc-badge-no"}>
                                        {isSolucionado(registroMapa.SOLUCIONADO) ? "Sí" : "No"}
                                    </span>
                                </div>
                            </div>
                            {registroMapa.OBSERVACIONES && (
                                <div className="desc-chip desc-chip--full">
                                    <i className="pi pi-comment desc-chip-icon" />
                                    <div>
                                        <span className="desc-chip-label">Observaciones</span>
                                        <span className="desc-chip-val">{registroMapa.OBSERVACIONES}</span>
                                    </div>
                                </div>
                            )}
                            <div className="desc-chip">
                                <i className="pi pi-globe desc-chip-icon" />
                                <div>
                                    <span className="desc-chip-label">Coordenadas</span>
                                    <span className="desc-chip-val">
                                        {registroMapa.LATITUD}, {registroMapa.LONGITUD}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Mapa Leaflet — tema blanco CARTO */}
                        <div className="desc-leaflet-wrapper">
                            <MapContainer
                                center={[
                                    parseFloat(registroMapa.LATITUD),
                                    parseFloat(registroMapa.LONGITUD),
                                ]}
                                zoom={15}
                                scrollWheelZoom
                                style={{ width: "100%", height: "100%" }}
                            >
                                <BaseMapLayer />
                                <Marker
                                    position={[
                                        parseFloat(registroMapa.LATITUD),
                                        parseFloat(registroMapa.LONGITUD),
                                    ]}
                                >
                                    <Popup>
                                        <strong>Desconexión</strong><br />
                                        Unidad: {registroMapa.ID_UNIDAD}<br />
                                        Operador: {registroMapa.OPERADOR}<br />
                                        {registroMapa.FECHA}
                                    </Popup>
                                </Marker>
                            </MapContainer>
                        </div>
                    </>
                )}
            </Dialog>

            {/* ── MODAL COMENTARIO ── */}
            <Dialog
                header={
                    <div className="desc-modal-header">
                        <span className="desc-modal-header-icon desc-modal-header-icon--blue">
                            <i className="pi pi-comment" />
                        </span>
                        <div>
                            <div className="desc-modal-header-title">Observación / Comentario</div>
                            {registroComentario && (
                                <div className="desc-modal-header-sub">
                                    Unidad {registroComentario.ID_UNIDAD} · {registroComentario.FECHA}
                                </div>
                            )}
                        </div>
                    </div>
                }
                visible={modalComentarioVisible}
                onHide={() => setModalComentarioVisible(false)}
                style={{ width: "min(480px, 97vw)" }}
                contentClassName="desc-modal-comentario"
                footer={footerComentario}
                draggable={false}
                resizable={false}
            >
                {registroComentario && (
                    <>
                        <div className="desc-comentario-meta">
                            <div className="desc-meta-item">
                                <i className="pi pi-car" />
                                <span>{registroComentario.ID_UNIDAD}</span>
                            </div>
                            <div className="desc-meta-item">
                                <i className="pi pi-user" />
                                <span>{registroComentario.OPERADOR ?? "—"}</span>
                            </div>
                            <div className="desc-meta-item">
                                <i className="pi pi-calendar" />
                                <span>{registroComentario.FECHA}</span>
                            </div>
                            <div className="desc-meta-item">
                                <i className="pi pi-tag" />
                                <span>{registroComentario.ASUNTO ?? "—"}</span>
                            </div>
                        </div>
                        <div className="desc-comentario-field">
                            <label className="desc-comentario-label">Observación</label>
                            <InputTextarea
                                value={comentario}
                                onChange={(e) => setComentario(e.target.value)}
                                rows={5}
                                autoResize
                                className="w-full"
                                placeholder="Escribe una observación o comentario..."
                            />
                        </div>
                    </>
                )}
            </Dialog>
        </div>
    );
};

export default Desconexiones;
